import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Paperclip, Trash, DownloadSimple, FilePdf, FileImage, FileXls, File as FileIcon, Plus } from "@phosphor-icons/react";

function iconFor(ct, name) {
  const n = (name || "").toLowerCase();
  if ((ct || "").startsWith("image/") || /\.(png|jpe?g|webp|gif)$/i.test(n)) return FileImage;
  if ((ct || "").includes("pdf") || n.endsWith(".pdf")) return FilePdf;
  if (n.endsWith(".xlsx") || n.endsWith(".xls") || n.endsWith(".csv")) return FileXls;
  return FileIcon;
}

function fmtBytes(b) {
  if (!b) return "0 B";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AttachmentsSection({ observationId, role, resolutionId, compact }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/observations/${observationId}/attachments`);
      setItems(resolutionId ? r.data.filter((a) => a.resolution_id === resolutionId) : r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (observationId) load();
    // eslint-disable-next-line
  }, [observationId, resolutionId]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (resolutionId) fd.append("resolution_id", resolutionId);
      await api.post(`/observations/${observationId}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      load();
      window.dispatchEvent(new Event("activities-reload"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAtt = async (id) => {
    if (!window.confirm("Remove this attachment?")) return;
    try {
      await api.delete(`/attachments/${id}`);
      toast.success("Removed");
      load();
      window.dispatchEvent(new Event("activities-reload"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const downloadAtt = async (att) => {
    try {
      const r = await api.get(`/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: att.content_type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  return (
    <div className={compact ? "" : "border border-slate-200 rounded-md p-3 bg-white"} data-testid="attachments-section">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
          <Paperclip size={11} /> Evidence Attachments {items.length > 0 && <span className="text-slate-400">({items.length})</span>}
        </div>
        <div>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={onUpload}
            data-testid="attachment-file-input"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="upload-attachment-btn"
          >
            <Plus size={12} className="mr-1" /> {uploading ? "Uploading..." : "Attach"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-slate-400">Loading...</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-slate-400 py-2" data-testid="no-attachments">No files attached yet.</div>
      ) : (
        <ul className="space-y-1.5">
          {items.map((att) => {
            const Icon = iconFor(att.content_type, att.original_filename);
            return (
              <li
                key={att.id}
                className="flex items-center justify-between gap-2 border border-slate-100 rounded px-2 py-1.5 hover:bg-slate-50"
                data-testid={`attachment-${att.id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Icon size={16} className="text-slate-500 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm truncate" title={att.original_filename}>{att.original_filename}</div>
                    <div className="text-[10px] text-slate-500">
                      {fmtBytes(att.size)} · {att.uploaded_by_role === "audit_team" ? "Auditor" : "Client"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => downloadAtt(att)}
                    className="p-1 hover:bg-slate-100 rounded text-slate-600"
                    title="Download"
                    data-testid={`download-attachment-${att.id}`}
                  >
                    <DownloadSimple size={14} />
                  </button>
                  <button
                    onClick={() => removeAtt(att.id)}
                    className="p-1 hover:bg-red-50 text-red-500 rounded"
                    title="Remove"
                    data-testid={`delete-attachment-${att.id}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
